<template>
    <div class="top-0 z-30 w-full from-white transition-colors to-very-light"
         :class="[isOpen ? 'bg-white bg-none' : 'bg-gradient-to-b', { 'fixed': !editMode }]">
        <nav class="container">
            <div class="hidden pb-4 text-sm lg:flex lg:space-x-6 lg:justify-end">
                <!--   META NAV desktop  -->
                <slot name="meta" />
                <!--   SITE NAV desktop  -->
                <slot name="site" />
                <div
                    v-if="langNav.length > 1 && !(langNav.length > 4)"
                    class="flex items-center space-x-1 py-3 text-gray">
                    <div v-for="(langItem, l) in langNav" :key="langItem.link">
                        <span v-if="langItem.active" class="text-red pr-1">{{ langItem.label }}</span>
                        <a v-else :href="langItem.link" class=" transition-colors hover:opacity-100 hover:text-red">
                            {{ langItem.label.replace(/_.*$/, '') }}
                        </a>
                        <span v-if="l < langNav.length - 1">|</span>
                    </div>
                </div>
            </div>
            <div class="flex items-center -mx-6 lg:justify-start lg:mx-0" :class="[$site !== 'fruechteboxexpress' ? 'lg:pb-5' : 'lg:pb-3',
                (!activePath.length && !activeMetaPath.length && isOpen) ? 'justify-end' : 'justify-between',
            ]">
                <a :href="homeLink" class="ml-4 lg:block lg:ml-0" :class="[logoClasses, { 'hidden': isOpen }]" :aria-label="homeLinkAlt">
                    <slot name="logo" />
                </a>
                <div class="flex lg:block lg:order-1 flex-shrink-0" :class="{ 'hidden': isOpen }">
                    <slot name="cta" />
                </div>
                <!--  burger and back button (mobile only)  -->
                <icon name="back"
                      v-if="(activePath.length || activeMetaPath.length || showSiteNav) && isOpen"
                      class="p-4 w-16 h-16 lg:hidden"
                      @click.native="back" />
                <div class="flex items-center gap-2">
                    <dropdown v-if="langNav.length > 1 && !isOpen" class="lg:hidden" alignment="right" show-icon>
                        <template #title>
                            <span class="font-medium">{{ langNav.find(l => l.active).label }}</span>
                        </template>
                        <dropdown-item v-for="(langItem, index) in langNav" :key="index" class="p-2">
                            <div v-if="langItem.active" class="text-red">
                                <span class="font-medium">{{ langItem.label.replace(/_.*$/, '') }}</span>
                                <span class="opacity-60"> | {{ langItem.displayLanguage }}</span>
                            </div>
                            <a v-else :href="langItem.link">
                                <span class="font-medium">{{ langItem.label.replace(/_.*$/, '') }}</span>
                                <span class="opacity-60"> | {{ langItem.displayLanguage }}</span>
                            </a>
                        </dropdown-item>
                    </dropdown>
                    <icon :name="isOpen ? 'close' : 'burger'"
                          class="p-4 w-16 h-16 lg:ml-auto lg:hidden"
                          @click.native="toggleMobile" />
                </div>
                <!--   MAIN NAV desktop (first level only)  -->
                <div class="hidden lg:mx-12 font-heading lg:block" :class="{ 'text-sm': !pelican }">
                    <slot />
                </div>
            </div>
            <collapse-transition>
                <div v-if="isOpen"
                     class="overflow-y-auto absolute left-0 top-full pb-4 w-full text-2xl font-bold bg-white border-opacity-10 lg:text-sm h-nav font-heading lg:h-auto lg:border-t border-gray lg:py-8">
                    <div class="container">
                        <!--   MAIN NAV (level 2+) mobile & expanded desktop -->
                        <template v-if="activeMetaPath.length === 0 && !showSiteNav">
                            <a v-if="activeItem.label"
                               :href="activeItem.link"
                               :target="activeItem.target"
                               class="block py-4 opacity-30 text-gray lg:hidden">{{ activeItem.label }}</a>
                            <ul class="lg:flex lg:space-x-16">
                                <template v-for="(item, i) in activeItem.items">
                                    <li v-if="item.items.length && item.level < 3"
                                        :key="item._uid"
                                        class="flex justify-between py-4 lg:flex-col lg:justify-start">
                                        <span class="block flex-1 lg:hidden" @click="activePath.push(i)">
                                            {{ item.label }}
                                        </span>
                                        <a :href="item.link" :target="item.target" @click.stop class="hidden lg:block hover:text-red lg:h-10">
                                            {{ item.label }}
                                        </a>
                                        <icon name="chevron" class="w-3 h-5 lg:hidden" />
                                        <ul class="hidden lg:block lg:pointer-events-auto">
                                            <li v-for="subItem in item.items" :key="subItem._uid" class="py-2">
                                                <a :href="subItem.link"
                                                   :target="subItem.target"
                                                   class="font-sans font-normal text-black opacity-60 hover:text-red">
                                                    {{ subItem.label }}
                                                </a>
                                            </li>
                                        </ul>
                                    </li>
                                    <a v-else
                                       :key="item._uid"
                                       :href="item.link"
                                       :target="item.target"
                                       class="block py-4 hover:text-red"
                                       :class="{ 'font-sans font-normal text-lg': item.level > 2 }">
                                        {{ item.label }}
                                    </a>
                                </template>
                            </ul>
                        </template>
                        <!--   META NAV mobile  -->
                        <template v-if="activePath.length === 0 && !showSiteNav">
                            <a v-if="activeMetaItem.label"
                               :href="activeMetaItem.link"
                               :target="activeMetaItem.target"
                               class="block py-4 text-lg opacity-30 text-gray lg:hidden">{{ activeMetaItem.label }}</a>
                            <ul class="font-sans text-lg font-normal text-black opacity-60 lg:hidden">
                                <template v-for="(item, i) in activeMetaItem.items">
                                    <li v-if="item.items.length"
                                        :key="item._uid"
                                        @click="activeMetaPath.push(i)"
                                        class="flex justify-between py-4">
                                        {{ item.label }}
                                        <icon name="chevron" class="w-3 h-5" />
                                    </li>
                                    <a v-else :key="item._uid" :href="item.link" :target="item.target" class="block py-4">
                                        {{ item.label }}
                                    </a>
                                </template>
                            </ul>
                        </template>
                        <div v-if="currentSite && activeMetaPath.length === 0 && activePath.length === 0 && !showSiteNav"
                             class="flex items-center py-4 font-sans text-lg font-normal lg:hidden"
                             @click="showSiteNav = true">
                            <icon :name="currentSite.icon"
                                  class="w-4 h-4 rounded-full overflow-hidden flex-grow-0 mr-2.5" />
                            <span class="text-black opacity-60">{{ currentSite.label }}</span>
                            <icon name="chevron" class="ml-auto w-3 h-5 opacity-60" />
                        </div>
                        <ul v-if="showSiteNav" class="font-sans text-lg font-normal lg:hidden">
                            <li v-for="item in siteItems" :key="item._uid">
                                <a :href="item.link" :target="item.target" class="flex items-center py-4">
                                    <icon :name="item.icon"
                                          class="w-4 h-4 rounded-full overflow-hidden flex-grow-0 mr-2.5" />
                                    {{ item.label }}
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </collapse-transition>
        </nav>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { Component, Prop, Provide, Watch } from 'vue-property-decorator';
import { CollapseTransition } from '@ivanv/vue-collapse-transition';
import Icon from '../base/Icon.vue';
import MainNavItem, { NavItem } from './MainNavItem.vue';

@Component({
    components: { CollapseTransition, Icon }
})
export default class MainNavigation extends Vue {
    @Provide('registerMainNavItem') childRegistration = this.register;
    @Provide('registerMetaNavItem') metaRegistration = this.registerMeta;
    @Provide('registerSiteNavItem') siteRegistration = this.registerSite;
    @Provide('mainNavTag') tagName = this.$vnode.tag;
    @Prop({ default: false }) editMode: boolean;
    @Prop({ default: false }) pelican: boolean;
    @Prop({ required: true }) site: string;
    @Prop({ required: true }) homeLink: string;
    @Prop({ required: true }) homeLinkAlt: string;
    @Prop() langNav: NavItem[];

    items: NavItem[] = [];
    metaItems: NavItem[] = [];
    siteItems: NavItem[] = [];
    overlay: HTMLElement = null;
    isOpen = false;
    showSiteNav = false;
    overlayTimer: NodeJS.Timeout = null;
    activePath = [];
    activeMetaPath = [];
    scrollY = 0;

    mounted() {
        document.addEventListener('scroll', this.onScroll, { passive: false });
    }

    beforeDestroy() {
        document.body.classList.remove('overflow-hidden');
        document.getElementById('app').classList.remove('lg:max-h-full');
        document.getElementById('app').classList.remove('lg:h-full');
        document.getElementById('app').classList.remove('max-h-screen');
        document.getElementById('app').classList.remove('h-screen');
        document.removeEventListener('scroll', this.onScroll);
    }

    private register(item) {
        this.items.push(item);
        item.$on('click', this.onItemClicked);
    }

    private registerMeta(item: NavItem) {
        this.metaItems.push(item);
    }

    private registerSite(item: NavItem) {
        this.siteItems.push(item);
    }

    back() {
        if (this.activePath.length) {
            this.activePath.pop();
        }
        if (this.activeMetaPath.length) {
            this.activeMetaPath.pop();
        }
        this.showSiteNav = false;
    }

    onItemClicked(item: MainNavItem) {
        const index = this.items.findIndex(x => x._uid === item._uid);
        if (index >= 0 && item.level === 1) {
            this.activePath = (this.activePath.length && this.activePath[0] === index) ? [] : [index];
            this.isOpen = !!this.activePath.length;
        }
    }

    toggleMobile() {
        this.isOpen = !this.isOpen;
        this.activePath = [];
        this.activeMetaPath = [];
        this.showSiteNav = false;
    }

    /**
     * Computed
     */
    get activeItem(): NavItem {
        let currentNode: NavItem = { label: '', target: '_self', items: [...this.items], level: 0, link: '', _uid: 0 };
        if (this.activePath.length) {
            this.activePath.forEach(index => {
                currentNode = currentNode.items[index];
            });
        }
        return currentNode;
    }

    get activeMetaItem(): NavItem {
        let currentNode: NavItem = { label: '', target: '_self', items: [...this.metaItems], level: 0, link: '', _uid: 0 };
        if (this.activeMetaPath.length) {
            this.activeMetaPath.forEach(index => {
                currentNode = currentNode.items[index];
            });
        }
        return currentNode;
    }

    get currentSite(): NavItem {
        return this.siteItems.find(x => x.icon === this.site);
    }

    get logoClasses(): string {
        switch (this.$site) {
            case 'fruechteboxexpress':
                return 'h-10';
            case 'pelicanrouge':
                return 'w-12 h-12 lg:h-24 lg:-mt-12 lg:w-28';
            default:
                return 'w-24 h-8 lg:w-28';
        }
    }

    /**
     * Watcher
     */
    @Watch('isOpen')
    onOpenToggled() {
        if (this.isOpen) {
            this.scrollY = window.scrollY;
            this.overlay = document.createElement('div');
            this.overlay.className = 'hidden fixed top-0 left-0 z-50 w-screen h-screen bg-black bg-opacity-20 opacity-0 transition-opacity lg:block';
            this.overlay.addEventListener('click', this.toggleMobile);
            this.$root.$el.prepend(this.overlay);
            clearTimeout(this.overlayTimer);
            this.overlayTimer = setTimeout(() => {
                if (this.overlay) {
                    this.overlay.classList.remove('opacity-0');
                    this.overlay.classList.add('opacity-100');
                }
            }, 200);
        } else if (this.overlay) {
            this.overlay.classList.remove('opacity-100');
            this.overlay.classList.add('opacity-0');
            this.overlay.removeEventListener('click', this.toggleMobile);
            clearTimeout(this.overlayTimer);
            this.overlayTimer = setTimeout(() => {
                this.overlay.remove();
                this.overlay = null;
            }, 500);
        }
        document.body.classList.toggle('overflow-hidden', this.isOpen);
        document.getElementById('app').classList.toggle('lg:max-h-full', this.isOpen);
        document.getElementById('app').classList.toggle('lg:h-full', this.isOpen);
        document.getElementById('app').classList.toggle('max-h-screen', this.isOpen);
        document.getElementById('app').classList.toggle('h-screen', this.isOpen);
        if (!this.isOpen) {
            window.scrollTo(0, this.scrollY);
            this.scrollY = 0;
        }
    }

    private onScroll(event: Event) {
        if (this.editMode) {
            return;
        }

        if (this.isOpen) {
            event.preventDefault();
        }
    }
}

</script>
